import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Heading } from '@americanexpress/dls-react';
import clsx from 'clsx';
import { useRoomsContext } from '../../contexts/roomsContext';
import RemoveRoomButton from '../RemoveRoomButton/RemoveRoomButton';
import bindRoomIndexToUpdateTravelersUtil from '../../utils/bindRoomIndexToUpdateTravelersUtil';
import StepperRow from '../StepperRow/StepperRow';
import ChildAgeNote from '../ChildrenAges/ChildAgeNote';
import {
  DEFAULT_ADULT_TRAVELING,
  DEFAULT_CHILDREN_TRAVELING,
  DEFAULT_INFANTS_TRAVELING,
  MAX_PETS,
  MIN_ADULT_TRAVELING,
  MIN_CHILDREN_TRAVELING,
  MIN_INFANTS_TRAVELING,
  MIN_PETS,
} from '../../constants/travelersConstants';
import { useConfigContext } from '../../contexts/configContext';
import NotesSection from '../NotesSection/NotesSection';
import { useStepperMaximum } from '../../hooks/useStepperMaximum';
import ChildrenAgesVariant from '../ChildrenAges/ChildrenAgesVariant';
import InfantsVariant from '../ChildrenAges/InfantsVariant';
import { useChildAgeRange } from '../../hooks/useChildAgeRange';

const RoomVariant = memo(({ isMultiRoom, canRemove, roomIndex }) => {
  const {
    rooms,
    updateAdults,
    updateChildCount,
    updateInfantCount,
    updatePets,
  } = useRoomsContext();

  const {
    hasChildrenAges,
    hasInfants,
    hasInfantsAges,
    hasPets,
    hasRooms,
    hasAdultAgesHint,
  } = useConfigContext();

  const { formatMessage } = useIntl();

  const {
    adults, children, infants, pets,
  } = rooms[roomIndex];

  const roomId = roomIndex + 1;

  const groupHeadingLabel = formatMessage({ id: 'roomNumber' }, { roomId });
  const globalisedAdultAria = formatMessage({ id: 'adult' });
  const globalisedChildAria = formatMessage({ id: 'child' });
  const globalisedInfantAria = formatMessage({ id: 'infant' });
  const { min, max } = useChildAgeRange();
  const {
    maxAdultsForStepper,
    maxChildrenForStepper,
    maxInfantsForStepper,
  } = useStepperMaximum(roomIndex);
  return (
    <div role="group" id="rooms-traveler-modal">
      {roomIndex > 0 && (
        <hr className="margin-b" data-testid="room-line-break" />
      )}
      {isMultiRoom && (
        <div
          className="flex flex-justify-between flex-align-center"
          style={{ height: '48px' }}
        >
          <Heading level={2} variant="sans-xsmall-bold">
            {groupHeadingLabel}
          </Heading>
          {canRemove && (
            <RemoveRoomButton classNames="margin-l" roomIndex={roomIndex} />
          )}
        </div>
      )}
      {hasRooms
      && (<NotesSection id="must_be_one_adult" />)}
      <StepperRow
        className={clsx(hasRooms ? 'pad-b' : 'margin-1-t')}
        defaultValue={DEFAULT_ADULT_TRAVELING}
        globalisedAria={globalisedAdultAria}
        isMultiRoom={isMultiRoom}
        max={maxAdultsForStepper}
        min={MIN_ADULT_TRAVELING}
        onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateAdults)}
        roomIndex={roomIndex}
        rowLabelId="adult"
        rowSubLabelId={hasAdultAgesHint ? 'adult_age_range' : null}
        rowSubLabelValues={{ min: Number.parseInt(max, 10) + 1 }}
        value={adults}
      />
      {!hasRooms && <hr className="margin-tb" />}
      <StepperRow
        className={hasRooms ? 'pad-b' : 'margin-tb'}
        defaultValue={DEFAULT_CHILDREN_TRAVELING}
        globalisedAria={globalisedChildAria}
        isMultiRoom={isMultiRoom}
        max={maxChildrenForStepper}
        min={MIN_CHILDREN_TRAVELING}
        onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateChildCount)}
        roomIndex={roomIndex}
        rowLabelId="child"
        rowSubLabelId="child_age_range"
        rowSubLabelValues={{ min, max }}
        value={children.length}
      />
      {hasChildrenAges && children.length > 0 && (
        <ChildrenAgesVariant isMultiRoom={isMultiRoom} roomIndex={roomIndex} />
      )}
      {hasInfants && (
        <>
          {!hasRooms && <hr className="margin-tb" />}
          <StepperRow
            className={hasRooms ? 'pad-b' : 'margin-tb'}
            defaultValue={DEFAULT_INFANTS_TRAVELING}
            globalisedAria={globalisedInfantAria}
            isMultiRoom={isMultiRoom}
            max={maxInfantsForStepper}
            min={MIN_INFANTS_TRAVELING}
            onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updateInfantCount)}
            roomIndex={roomIndex}
            rowLabelId="infant"
            rowSubLabelId="age_under_2"
            value={infants.length}
          />
          {hasInfantsAges && infants.length > 0 && (
            <ChildAgeNote infants={infants}>
              <InfantsVariant isMultiRoom={isMultiRoom} roomIndex={roomIndex} />
            </ChildAgeNote>
          )}
        </>
      )}
      {!isMultiRoom && hasRooms && (
        <hr />
      )}
      {hasPets && (
        <>
          {!hasRooms && <hr className="margin-tb" />}
          <StepperRow
            className="margin-b"
            defaultValue={MIN_PETS}
            globalisedAria={formatMessage({ id: 'pets' })}
            isMultiRoom={false}
            max={MAX_PETS}
            min={MIN_PETS}
            onChange={bindRoomIndexToUpdateTravelersUtil(roomIndex, updatePets)}
            roomIndex={roomIndex}
            rowLabelId="pet"
            value={pets}
          />
          <NotesSection id="pet_service_animals_notice" />
        </>
      )}
    </div>
  );
});

RoomVariant.displayName = 'Room';

RoomVariant.propTypes = {
  canRemove: PropTypes.bool,
  isMultiRoom: PropTypes.bool,
  roomIndex: PropTypes.number.isRequired,
};

RoomVariant.defaultProps = {
  canRemove: false,
  isMultiRoom: false,
};

export default RoomVariant;
